import { limitsStatusContract2Document } from '../limits-contract2.ts';
import type {
  FetchMatcher,
  JsonApiDoc,
  JsonApiResource,
  MatcherFixture,
} from 'tio-data/demo/types';
import { assertNever, type DemoFixtureIdentity } from 'tio-employee/demo/types';
import {
  getDemoTaProgramsForClient,
  DEMO_TA_PRIMARY_TEMPLATE_ID,
  DEMO_TA_PROGRAM_CODES,
  type DemoTaProgram,
  type DemoTaPaymentModelId,
} from 'tio-employee/utils/career-pathways-demo-data';

/**
 * Fixtures for the "in-progress" Tuition Assistance demo: an active participant
 * with an active eligibility on a launched program template, one active program
 * instance, and one in-progress application with one course. Shapes follow the
 * proven demo push payloads from the TAS getting-started flow.
 *
 * Served through the demo handler for the store-routed TAS pages
 * (tas/getting-started, tas/programs, tas/applications/*).
 *
 * Employer/employee names derive from the {@link DemoFixtureIdentity} the
 * registry resolves from the active selection, so the TAS surfaces (dashboard,
 * approval queue) always agree with the session identity — never a hardcoded
 * "Acme Corporation" against a "Lifepoint Health" header.
 *
 * The application/course/company-setting fixtures are shaped by the selected
 * TA payment model (reimbursement / prepayment-CourseCash / deferral), so the
 * classic admin drill-ins (approval page, application show) reflect the model
 * instead of always rendering reimbursement (the always-rendered-reimbursement seam gap). The
 * drill-ins read the JSON:API MODEL GETTERS, so each branch fabricates exactly
 * what those getters consume — all monetary values in CENTS:
 * - model.paymentMethod        <- fields.PAYMENT_TYPE_FOR_DIRECT_PAY
 * - model.cardPreApprovedTotal <- fields.CARD_PRE_APPROVED_TOTAL (UNsuffixed —
 *   the `…Integer` field family belongs to the OpenSearch queue/list hits,
 *   not the JSON:API model; mixing them up renders $0)
 * - course-cash balances       <- amounts.course_cash.{reserved,used,refunded}
 * - requested/expense rows     <- the linked tas-course's COURSE_* fields
 * Course naming mirrors the reimagined snapshot plane
 * (reimagined-data#getDemoTasData) so both demo planes tell one story.
 */

// One ACTIVE eligibility per catalog code — the eligibility maps and the
// participant's tas-eligibilities must cover EVERY program or the dashboard
// filters that program's card off.
const ELIGIBILITY_RESOURCES: JsonApiResource[] = DEMO_TA_PROGRAM_CODES.map((code, index) => ({
  type: 'tas-eligibility',
  id: `demo-tas-eligibility-${index + 1}`,
  attributes: { code, state: 'TAS.EligibilityState.ACTIVE' },
}));

function buildTemplateResource(program: DemoTaProgram): JsonApiResource {
  return {
    type: 'tas-program-template',
    id: program.id,
    attributes: {
      code: program.code,
      state: 'TAS.ProgramTemplateState.LAUNCHED',
      fields: {
        PROGRAM_NAME: { values: [program.name] },
        PROGRAM_DESCRIPTION: { values: [program.description] },
        TYPE: { values: [program.typeClassification] },
        DEPENDENT_PROGRAM: { values: [false] },
        MULTIPLE_INSTANCE_PROGRAM: { values: [false] },
        ELIGIBILITY_WAITING_PERIOD: { values: [0] },
        ...(program.isDirectPayProgram
          ? {
              // CourseCa$h™ (direct-pay) program: the same field pair a live
              // prepaid template carries — drives isDirectPayProgram /
              // isPrepaidProgram and the CourseCash chips.
              PROGRAM_PAYMENT: { values: ['ON_ATTEND'] },
              PAYMENT_TYPE_FOR_DIRECT_PAY: { values: ['TAS.AssistanceManner.COURSE_CASH'] },
            }
          : {}),
      },
      // Sales-facing program FAQs (rendered by TasFaqs on getting-started,
      // applications/show, and programs/details).
      faqs: program.faqs,
      customFields: [],
      approverInstructions: [],
      isTrial: false,
    },
    relationships: {
      conditions: { data: [] },
      tasEligibilities: { data: [] },
      tasGroupings: { data: [] },
      tasProgramInstances: { data: [] },
      tasFieldOptions: { data: [] },
      tasGroups: { data: [] },
      // Admin application/template screens read template.company.companySetting.
      company: { data: { type: 'company', id: 'demo-tas-company-1' } },
    },
  };
}

/** demo-tas-template-N → demo-tas-instance-N (stable per-program instance ids). */
function instanceIdFor(templateId: string): string {
  return templateId.replace('template', 'instance');
}

// The approval + admin application-show surfaces walk
// instance.employee(.person/.company/.tasParticipant), so each instance links
// the demo employee; empty hasMany linkage keeps asset/attachment readers on
// a clean empty state instead of crashing on undefined. The walkthrough
// program's instance is PROGRAM_APPROVED (it carries the seeded draft +
// submitted applications); every other program's instance is a DRAFT — the
// state a freshly-started program has, so `apply` lands on the program form
// (programs/show bounces isDraft instances to programs/new).
function buildInstanceResource(program: DemoTaProgram): JsonApiResource {
  const isPrimary = program.id === DEMO_TA_PRIMARY_TEMPLATE_ID;
  return {
    type: 'tas-program-instance',
    id: instanceIdFor(program.id),
    attributes: {
      state: isPrimary
        ? 'TAS.ProgramInstanceState.PROGRAM_APPROVED'
        : 'TAS.ProgramInstanceState.DRAFT',
      // The program details page filters instance.customFields unguarded, and
      // the progress tracker walks instance.transitionLog.some(...) unguarded
      // — both must exist (empty is fine for a just-started draft).
      customFields: [],
      fields: {},
      // Log EVENTS are snake_case on the wire — programSubmissionDate finds
      // 'request_program_approval' (tas-program-instance.ext), NOT the
      // dasherized transition-endpoint slug.
      transitionLog: isPrimary
        ? [
            {
              event: 'request_program_approval',
              from: 'TAS.ProgramInstanceState.DRAFT',
              to: 'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL',
              timestamp: '2026-05-10T14:00:00Z',
              reason: null,
            },
            {
              event: 'approve_program',
              from: 'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL',
              to: 'TAS.ProgramInstanceState.PROGRAM_APPROVED',
              timestamp: '2026-05-12T14:00:00Z',
              reason: null,
            },
          ]
        : [],
    },
    relationships: {
      tasProgramTemplate: { data: { type: 'tas-program-template', id: program.id } },
      employee: { data: { type: 'employee', id: 'demo-tas-employee-1' } },
      tasAssets: { data: [] },
      attachments: { data: [] },
      // The program show page reads instance.tasApplications.length unguarded;
      // the walkthrough instance links its in-flight application (sideloaded by
      // the doc builders), the startable drafts have none yet.
      tasApplications: {
        data: isPrimary ? [{ type: 'tas-application', id: 'demo-tas-application-1' }] : [],
      },
    },
  };
}

const PARTICIPANT_RESOURCE: JsonApiResource = {
  type: 'tas-participant',
  id: 'demo-tas-participant-1',
  attributes: { state: 'TAS.ParticipantState.ACTIVE', employmentStartDate: '2022-01-15' },
  relationships: {
    tasEligibilities: {
      data: ELIGIBILITY_RESOURCES.map((eligibility) => ({
        type: 'tas-eligibility',
        id: eligibility.id as string,
      })),
    },
    tasGroupings: { data: [] },
  },
};

// --- Payment-model shaping ---------------------------------------------------

// Course expenses in CENTS; they sum to $1,466.45, the walkthrough's requested
// total. Under prepayment the tuition is the CourseCash-covered part
// (CARD_PRE_APPROVED_TOTAL) while books/fees remain reimbursable, so the
// approval page shows the real CourseCash-vs-reimbursement split.
const COURSE_TUITION_CENTS = 118_450; // $1,184.50
const COURSE_BOOKS_CENTS = 21_375; // $213.75
const COURSE_FEES_CENTS = 6_820; // $68.20

// Mirrors the snapshot plane's per-model naming (reimagined-data
// getDemoTasData: 'Coursera / Google (CourseCash)' / 'Riverview LPN School
// (deferral-eligible)'), so the employee reimagined pages and the store-routed
// drill-ins narrate the same demo.
function courseNameFor(paymentModelId: DemoTaPaymentModelId): string {
  switch (paymentModelId) {
    case 'prepayment':
      return 'Google Data Analytics (CourseCash)';
    case 'reimbursement-with-deferral':
      return 'LPN to RN Bridge — Riverview LPN School (deferral-eligible)';
    case 'reimbursement':
      return 'Intro to Data Science';
    default:
      return assertNever(paymentModelId);
  }
}

// The expense table, requested totals, and the CourseCash subtotal on the
// approval page all derive from tas-course COURSE_* fields (fields.REQUESTED_TOTAL
// on the application has no model reader) — without a course every amount
// renders zero. The course's tasApplication back-link is deliberately omitted:
// the inverse is wired when the application's `tasCourses` linkage is pushed,
// which also stays correct when the handler echoes a requested application id.
function buildCourseResource(paymentModelId: DemoTaPaymentModelId): JsonApiResource {
  return {
    type: 'tas-course',
    id: 'demo-tas-course-1',
    attributes: {
      isActive: true,
      customFields: [],
      fields: {
        COURSE_NAME: { values: [courseNameFor(paymentModelId)] },
        COURSE_NUMBER: { values: ['DEMO-101'] },
        COURSE_TUITION: { values: [COURSE_TUITION_CENTS] },
        COURSE_BOOKS: { values: [COURSE_BOOKS_CENTS] },
        COURSE_FEES: { values: [COURSE_FEES_CENTS] },
        COURSE_LABS: { values: [0] },
        COURSE_SOFTWARE: { values: [0] },
        COURSE_CREDIT: { values: [3] },
      },
    },
    relationships: {
      tasAssets: { data: [] },
      attachments: { data: [] },
    },
  };
}

// The admin financial summary's CourseCash section reads the application's
// pcsCards for the card-status chip. CREATED renders "Pending Activation",
// coherent with a pending-approval application. The employee card display
// (stripe-card-display) guards its Stripe issuing-elements setup behind
// card.pcsCompany.providerId, which this resource deliberately omits, so demo
// never reaches the issuing flow or its raw `/pcs-cards/:id/client-token`
// fetch. The tasApplication back-link is inverse-wired from the application.
const PCS_CARD_RESOURCE: JsonApiResource = {
  type: 'pcs-card',
  id: 'demo-pcs-card-1',
  attributes: {
    state: 'PCS.CardState.CREATED',
    program: 'PCS.Program.COURSE_CASH',
    amounts: {
      authorization: {
        approved: 0,
        captured: 0,
        created: 0,
        declined: 0,
        expired: 0,
        requested: 0,
        reversed: 0,
      },
      dispute: { created: 0 },
      program: { approved: COURSE_TUITION_CENTS },
      transaction: { captured: 0, created: 0, refunded: 0 },
    },
  },
  relationships: {
    pcsAuthorizations: { data: [] },
    pcsCardEvents: { data: [] },
    pcsDisputes: { data: [] },
    pcsTransactions: { data: [] },
  },
};

// Application states used by the walkthrough: the employee CONTINUES a
// DEFINE_COURSES draft and submits it; the approver APPROVES a matching
// PENDING_COURSES_APPROVAL application in their queue.
const STATE_DRAFT = 'TAS.ApplicationState.DEFINE_COURSES';
const STATE_PENDING = 'TAS.ApplicationState.PENDING_COURSES_APPROVAL';

// Every async:false hasMany the approval screens iterate is linked empty —
// an OMITTED linkage materializes as undefined and crashes `.find(...)` /
// `.filter(...)` readers, while an empty one renders the empty state.
function buildApplicationResource(
  paymentModelId: DemoTaPaymentModelId,
  state: string = STATE_PENDING
): JsonApiResource {
  const isPrepayment = paymentModelId === 'prepayment';
  const isDraft = state === STATE_DRAFT;

  return {
    type: 'tas-application',
    id: 'demo-tas-application-1',
    attributes: {
      state,
      // Nothing is paid pre-approval under ANY payment model. Prepayment
      // differs by the CourseCash fields + card below; deferral differs by
      // withholding payment AFTER approval, which an in-progress application
      // cannot exhibit yet — richer deferral payment states arrive with the
      // 'approved' TAS sub-scenario (#8795).
      paymentState: 'TAS.ApplicationPaymentState.NONE',
      fields: {
        // The guided wizard's step-1 completeness gate requires APPLICATION_NAME
        // (hardcoded required in step-education-request); without it the draft
        // opens on an empty step 1 instead of walking straight to Submit.
        APPLICATION_NAME: { values: [`Fall 2026 — ${courseNameFor(paymentModelId)}`] },
        COURSE_NAME: { values: [courseNameFor(paymentModelId)] },
        REQUESTED_TOTAL: { values: [1500] },
        ...(isPrepayment
          ? {
              // model.paymentMethod — flips every CourseCash gate on the
              // approval surfaces.
              PAYMENT_TYPE_FOR_DIRECT_PAY: { values: ['TAS.AssistanceManner.COURSE_CASH'] },
              // model.cardPreApprovedTotal, in CENTS (the surfaces format /100).
              CARD_PRE_APPROVED_TOTAL: { values: [COURSE_TUITION_CENTS] },
            }
          : {}),
      },
      customFields: [],
      // ApplicationAmounts in CENTS, fully formed for EVERY model:
      // stripe-card-display reads amounts.course_cash UNGUARDED, so the block
      // must exist the moment a pcs-card makes that component render.
      amounts: {
        requested: COURSE_TUITION_CENTS + COURSE_BOOKS_CENTS + COURSE_FEES_CENTS,
        approved_for_payment: 0,
        course_cash: { reserved: 0, used: 0, refunded: 0 },
        pending_payment: 0,
        paid: 0,
        refund_requested: 0,
        refund_paid_back: 0,
        refund_forgiven: 0,
        refund_cancelled: 0,
        refund_limit: 0,
        flags: { request_payment_enabled: false, request_repayment_enabled: false },
      },
      // The approval-queue status widgets read the state-matching approval
      // summary (`coursesApproval.possible_reviewers` etc.) without guarding.
      coursesApproval: {
        affirmed: [],
        current_reviewers: [],
        possible_reviewers: [],
        required: [],
        approved: false,
      },
      // The approval surfaces derive submission/approval dates from these logs
      // via `.find(...)` — they must exist (empty is fine; undefined crashes).
      // A DEFINE_COURSES draft has not been submitted yet, so it carries no
      // submit event; the pending (approver) application does.
      transitionLog: isDraft
        ? []
        : [
            {
              event: 'submit',
              from: STATE_DRAFT,
              to: STATE_PENDING,
              timestamp: '2026-05-20T14:00:00Z',
              reason: null,
            },
          ],
      paymentStateLog: [],
      approvedTotalLog: [],
    },
    relationships: {
      tasProgramInstance: { data: { type: 'tas-program-instance', id: 'demo-tas-instance-1' } },
      tasCourses: { data: [{ type: 'tas-course', id: 'demo-tas-course-1' }] },
      tasAssistances: { data: [] },
      tasAssets: { data: [] },
      attachments: { data: [] },
      aiRecommendations: { data: [] },
      pcsCards: { data: isPrepayment ? [{ type: 'pcs-card', id: 'demo-pcs-card-1' }] : [] },
      tags: { data: [] },
    },
  };
}

// company-setting drives several dashboard reads
// (currentEmployee.company.companySetting.tuitionAssistance.*). The direct-pay
// and card-review keys are the employer-level CourseCash gates, mirroring
// buildDemoCompanySettings (career-pathways-demo-data.ts), so the store plane
// and the partner-service blob agree on whether CourseCash UI is enabled.
function buildCompanySettingResource(paymentModelId: DemoTaPaymentModelId): JsonApiResource {
  const isPrepayment = paymentModelId === 'prepayment';
  return {
    type: 'company-setting',
    id: 'demo-tas-companysetting-1',
    attributes: {
      tuitionAssistance: {
        hideAnnualBenefitsCard: false,
        isDependentProgramsEnabled: false,
        isDirectPayEnabled: isPrepayment,
        isCardUsageReviewEnabled: isPrepayment,
      },
    },
  };
}

function buildCompanyResource(employerName: string): JsonApiResource {
  return {
    type: 'company',
    id: 'demo-tas-company-1',
    attributes: { name: employerName, isActive: true },
    relationships: {
      companySetting: { data: { type: 'company-setting', id: 'demo-tas-companysetting-1' } },
    },
  };
}

// Employee findRecord (include tas-participant,company) — the dashboard reads
// employee.tasParticipant to decide whether to run eligibility checks, so the
// participant must be sideloaded for the demo program to surface. Its
// company/company-setting/participant ids are reused by demo/identity-fixtures.ts
// (buildDemoIdentityDoc) for the demo session identity — and its NAMES derive
// from the same identity, so this later push can never rename the session user.
function buildEmployeeResource(identity: DemoFixtureIdentity): JsonApiResource {
  return {
    type: 'employee',
    id: 'demo-tas-employee-1',
    attributes: {
      firstName: identity.firstName,
      lastName: identity.lastName,
      isActive: true,
      // Admin application-details reads customAttributes.raw?... without
      // guarding customAttributes itself.
      customAttributes: {},
      employmentType: 'FULL_TIME',
    },
    relationships: {
      tasParticipant: { data: { type: 'tas-participant', id: 'demo-tas-participant-1' } },
      company: { data: { type: 'company', id: 'demo-tas-company-1' } },
    },
  };
}

const participantCollection: JsonApiDoc = {
  data: [PARTICIPANT_RESOURCE],
  included: [...ELIGIBILITY_RESOURCES],
};

// Empty OpenSearch result — a valid "enrolled, no applications yet" state.
// executeSearchRequest is a store.request builder (POST, SkipCache), so the
// demo HANDLER intercepts it (not the fetch interceptor); callers read .content.
const EMPTY_SEARCH = {
  took: 0,
  timedOut: false,
  hits: { total: { value: 0, relation: 'eq' }, maxScore: 0, hits: [] },
  aggregations: {},
};

// --- Admin TAS approval queue (OpenSearch /api/v2/search, model-type
// TasProgramInstance) -------------------------------------------------------
// The admin approval queue (routes/.../admin/tas/approval-queue) POSTs an
// OpenSearch query and renders `OpenSearchResponseBuilder.from(res).toPaginatedResult()`.
// Each hit's `_source` is a TasProgramInstance doc; the queue row reads
// employeeNameKeyword / employeePayrollIdKeyword / companyName / state / dates.
interface QueueAppSource {
  id: string;
  employeeName: string;
  payrollId: string;
  employeeId: number;
  personId: number;
  state: string;
  submitted: string;
  priority: string;
}

function queueHit(app: QueueAppSource, employerName: string, programName: string) {
  return {
    _index: 'tas_program_instances',
    _id: app.id,
    _score: 1,
    _source: {
      id: app.id,
      recordType: 'application',
      companyId: 'demo-tas-company-1',
      companyName: employerName,
      employeeId: app.employeeId,
      personId: app.personId,
      employeeNameKeyword: app.employeeName,
      employeePayrollIdKeyword: app.payrollId,
      tasApplicationIdKeyword: `${app.id}-application`,
      tasProgramInstanceIdKeyword: `${app.id}-instance`,
      tasApplicationStateKeyword: app.state,
      tasApplicationCreatedAtDate: app.submitted,
      tasApplicationAttentionPriorityDate: app.priority,
      tasApplicationLatestSubmissionTs: `${app.submitted}T14:00:00Z`,
      tasApplicationLatestStateChangeAtDate: app.submitted,
      tasProgramTemplateFieldProgramNameKeyword: programName,
    },
  };
}

const APPROVAL_QUEUE_APPS: QueueAppSource[] = [
  {
    id: 'demo-queue-1',
    employeeName: 'Jordan Lee',
    payrollId: 'EMP-1001',
    employeeId: 1001,
    personId: 2001,
    state: 'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
    submitted: '2026-05-20',
    priority: '2026-05-21',
  },
  {
    id: 'demo-queue-2',
    employeeName: 'Priya Natarajan',
    payrollId: 'EMP-1042',
    employeeId: 1042,
    personId: 2042,
    state: 'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
    submitted: '2026-05-22',
    priority: '2026-05-23',
  },
  {
    id: 'demo-queue-3',
    employeeName: 'Marcus Brown',
    payrollId: 'EMP-1088',
    employeeId: 1088,
    personId: 2088,
    state: 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
    submitted: '2026-05-24',
    priority: '2026-05-25',
  },
];

export function buildInProgressTasMatchers(
  identity: DemoFixtureIdentity,
  paymentModelId: DemoTaPaymentModelId
): MatcherFixture[] {
  // The client's program catalog (industry-aware slot 3). The FIRST program is
  // the walkthrough one — the seeded draft + submitted applications, the
  // approval queue, and the approved instance all hang off it.
  const catalog = getDemoTaProgramsForClient(identity.clientId);
  const templateResources = catalog.map(buildTemplateResource);
  const instanceResources = catalog.map(buildInstanceResource);
  const templateResource = templateResources[0] as JsonApiResource;
  const primaryInstance = instanceResources[0] as JsonApiResource;
  const programName = catalog[0]?.name ?? 'Tuition Reimbursement';
  const companyResource = buildCompanyResource(identity.employerName);
  const companySettingResource = buildCompanySettingResource(paymentModelId);
  const employeeResource = buildEmployeeResource(identity);
  // Op-seam reads (classic admin drill-in, list reads) get the submitted
  // (PENDING) application. The employee's DEFINE_COURSES DRAFT and the approver's
  // PENDING queue applications are served — split by id — on the fetch seam
  // (buildInProgressTasFetchMatchers), which is where the shipped detail /
  // continue-draft / approval routes actually read.
  const applicationResource = buildApplicationResource(paymentModelId);
  const courseResource = buildCourseResource(paymentModelId);

  const employeeDoc: JsonApiDoc = {
    data: employeeResource,
    included: [
      PARTICIPANT_RESOURCE,
      ...ELIGIBILITY_RESOURCES,
      companyResource,
      companySettingResource,
    ],
  };
  const templateCollection: JsonApiDoc = {
    data: [...templateResources],
    included: [companyResource, companySettingResource],
  };
  const templateDoc: JsonApiDoc = {
    data: templateResource,
    included: [companyResource, companySettingResource],
  };
  // The instance links the demo employee, so every doc that sideloads it also
  // sideloads the employee graph (participant/eligibility/company/setting) —
  // async:false relationships resolve empty unless included.
  const employeeGraph = [
    employeeResource,
    PARTICIPANT_RESOURCE,
    ...ELIGIBILITY_RESOURCES,
    companyResource,
    companySettingResource,
  ];
  // ACTIVE-instance queries serve ONLY the walkthrough program's approved
  // instance. The other programs' DRAFT instances exist solely behind their
  // per-id reads (they materialize when apply-for-assistance returns their id)
  // — listing them here would make every catalog card read as "already
  // enrolled" and kill the start-application guards.
  const instanceCollection: JsonApiDoc = {
    data: [primaryInstance],
    included: [templateResource, ...employeeGraph, applicationResource, courseResource],
  };
  const instanceDoc: JsonApiDoc = {
    data: primaryInstance,
    included: [templateResource, ...employeeGraph, applicationResource, courseResource],
  };
  // Sideload the instance AND its template so async:false
  // tasProgramInstance.tasProgramTemplate resolves on the application show/confirmation routes.
  // The course (and the CourseCash card under prepayment) ride along for the
  // same reason — the expense table and card chip read them async:false.
  const applicationGraph = [
    courseResource,
    ...(paymentModelId === 'prepayment' ? [PCS_CARD_RESOURCE] : []),
    primaryInstance,
    templateResource,
    ...employeeGraph,
  ];
  const applicationDoc: JsonApiDoc = {
    data: applicationResource,
    included: applicationGraph,
  };
  // Application list reads (e.g. the employee's applications on the admin
  // drill-in) get the same in-progress application.
  const applicationCollection: JsonApiDoc = {
    data: [applicationResource],
    included: applicationGraph,
  };

  const approvalQueueSearch = {
    took: 0,
    timedOut: false,
    hits: {
      total: { value: APPROVAL_QUEUE_APPS.length, relation: 'eq' },
      maxScore: 1,
      hits: APPROVAL_QUEUE_APPS.map((app) => queueHit(app, identity.employerName, programName)),
    },
    aggregations: {},
  };

  return [
    // The admin approval-queue search (model-type TasProgramInstance) gets demo
    // applications; this MUST precede the generic empty-search matcher below
    // (first-match-wins), which still serves every other search (e.g. reporting).
    {
      matcher: {
        kind: 'builder',
        method: 'POST',
        urlMatches: (url) => /\/api\/v2\/search(?:\?|$)/.test(url),
        bodyMatches: (body) =>
          (body as { 'model-type'?: string } | undefined)?.['model-type'] === 'TasProgramInstance',
      },
      content: approvalQueueSearch,
    },
    // Base /api/v2/search POSTs only — never the /api/v2/search/* metadata
    // subroutes (mapping/fields/...), matching the registry's precise pattern.
    {
      matcher: {
        kind: 'builder',
        method: 'POST',
        urlMatches: (url) => /\/api\/v2\/search(?:\?|$)/.test(url),
      },
      content: EMPTY_SEARCH,
    },
    // "Start application": initializeTypedProgramRequest POSTs
    // /tas-program-templates/:id/apply-for-assistance (SkipCache builder) and
    // reads the created instance id off `content.data.id`. One matcher per
    // catalog program returns that program's own draft instance, so every
    // eligible-benefits card starts into its own program form instead of
    // erroring on the network-free 204.
    ...catalog.map((program): MatcherFixture => ({
      matcher: {
        kind: 'builder',
        method: 'POST',
        urlIncludes: `/tas-program-templates/${program.id}/apply-for-assistance`,
      },
      content: { data: { id: instanceIdFor(program.id), type: 'tas-program-instance' } },
    })),
    // Program transition history (programs/history + drill-ins):
    // getTasProgramInstanceHistoryRequest is a SkipCache GET builder — served
    // here so the history page maps over clean empty logs instead of crashing
    // on the inert block body.
    {
      matcher: {
        kind: 'builder',
        method: 'GET',
        urlMatches: (url) => /\/tas-program-instances\/[^/]+\/history(?:\?|$)/.test(url),
      },
      content: { data: { assistance_history: [], application_history: [] } },
    },
    // TA Mentoring request form (tas/mentoring): submitTasCoachingRequest POSTs
    // /coaching/schedule-tas (SkipCache builder). A matched empty body resolves
    // the task so the form shows its success state instead of a submit error.
    {
      matcher: { kind: 'builder', method: 'POST', urlIncludes: '/coaching/schedule-tas' },
      content: {},
    },
    { matcher: { kind: 'op', op: 'findRecord', type: 'employee' }, docs: [employeeDoc] },
    {
      matcher: { kind: 'op', op: 'query', type: 'tas-participant' },
      docs: [participantCollection],
    },
    // The dashboard runs a SECOND tas-program-template query for trial templates
    // (admins only). It must NOT return the launched demo program, or the eligible
    // list shows a duplicate card. This filter-specific matcher precedes the
    // generic one below (first match wins) and serves an empty collection.
    {
      matcher: {
        kind: 'op',
        op: 'query',
        type: 'tas-program-template',
        where: (query) => (query?.filter as { isTrial?: boolean } | undefined)?.isTrial === true,
      },
      docs: [{ data: [] }],
    },
    {
      matcher: { kind: 'op', op: 'query', type: 'tas-program-template' },
      docs: [templateCollection],
    },
    {
      matcher: { kind: 'op', op: 'findRecord', type: 'tas-program-template' },
      docs: [templateDoc],
    },
    {
      matcher: { kind: 'op', op: 'query', type: 'tas-program-instance' },
      docs: [instanceCollection],
    },
    // tas/programs loads its primary model via findRecord('tas-program-instance', ...).
    {
      matcher: { kind: 'op', op: 'findRecord', type: 'tas-program-instance' },
      docs: [instanceDoc],
    },
    { matcher: { kind: 'op', op: 'query', type: 'tas-group' }, docs: [{ data: [] }] },
    { matcher: { kind: 'op', op: 'findRecord', type: 'tas-application' }, docs: [applicationDoc] },
    {
      matcher: { kind: 'op', op: 'query', type: 'tas-application' },
      docs: [applicationCollection],
    },
  ];
}

// --- Raw-fetch (seam B) matchers -------------------------------------------
// The TAS dashboard reaches these directly via fetch (bypassing the store):
// limits status and the tas-participant eligibility endpoints. (OpenSearch is
// NOT here — executeSearchRequest is a store.request builder, so it is served
// by the demo handler via the '/api/v2/search' builder matcher above.)

const limitsDetail = (limit: number | null, usage: number) => ({
  annual_limit: limit,
  annual_usage: usage,
  available: limit === null ? null : limit - usage,
  hard: limit !== null,
  limit,
  one_month_limit: null,
  one_month_usage: 0,
  six_month_limit: null,
  six_month_usage: 0,
  taxable_limit: null,
  three_month_limit: null,
  three_month_usage: 0,
  usage,
});

// All amounts are CENTS — the same units the app compares against
// (`application.requestedTotal` is pennies), so the submit-time limits gate
// (`validateAmountRequestedAgainstCurrentLimitsStatus`: available − requestedTotal ≥ 0)
// passes. $5,250 annual / $1,500 used / $3,750 available.
const DEMO_LIMITS = limitsStatusContract2Document([
  {
    annual_start_date: '2026-01-01',
    annual_end_date: '2026-12-31',
    available: 337_575,
    available_with_override: 337_575,
    details: {
      company: limitsDetail(null, 0),
      product: limitsDetail(525_000, 187_425),
      batch: limitsDetail(null, 0),
      scheme: limitsDetail(null, 0),
    },
    employee_id: 'demo-tas-employee-1',
    hard_limit: 'product_annual',
    limit_capacity: 'TAS.LimitCapacity.MONETARY',
    product: 'TA',
    scheme: null,
    soft_limit: 'none',
  },
]);

// Limits-summary tree consistent with DEMO_LIMITS: TA capped at $5,250/yr with
// $1,500 used → $3,750 remaining. Feeds the tree-sourced benefit cards
// (tio-common BenefitSummaryCards), which fetch /api/v2/limits/summary.
const DEMO_LIMITS_SUMMARY = {
  data: {
    summary: {
      db_limit: null,
      db_annual_limit: null,
      aggregate_limit: null,
      aggregate_annual_limit: 525_000,
      effective_limit: null,
      effective_annual_limit: 525_000,
      used: 150_000,
      annual_used: 187_425,
      remaining: 337_575,
    },
    tree: {
      'demo-company-1': {
        level: 0,
        label: 'Company',
        children: ['TA'],
        eligible: true,
        db_limit: null,
        db_annual_limit: null,
        aggregate_limit: null,
        aggregate_annual_limit: 525_000,
        effective_limit: null,
        effective_annual_limit: 525_000,
        used: 150_000,
        annual_used: 187_425,
        remaining: 337_575,
      },
      TA: {
        level: 1,
        label: 'TA',
        children: [],
        eligible: true,
        db_limit: null,
        db_annual_limit: 525_000,
        aggregate_limit: null,
        aggregate_annual_limit: null,
        effective_limit: null,
        effective_annual_limit: 525_000,
        used: 150_000,
        annual_used: 187_425,
        remaining: 337_575,
        binding_limit: 525_000,
        binding_used: 187_425,
        binding_period: 'annual',
      },
    },
    meta: {
      employee_id: 'demo-tas-employee-1',
      company_id: 'demo-company-1',
      as_of_date: '2026-07-01',
      annual_start_date: '2026-01-01',
      annual_end_date: '2027-01-01',
    },
  },
};

/**
 * Clone a single-resource doc with the REQUESTED id echoed into the primary
 * resource (and every included back-reference repointed) — the fetch-seam
 * mirror of the request handler's `withEchoedId`. The shipped TAS detail
 * route reaches this seam via a builder findRecord whose ids can come from
 * OUTSIDE this fixture plane (links rendered by the legacy reimagined-data
 * mock plane carry ids like `demo-app-1`), so the fixture must answer under
 * whatever id was asked.
 */
function echoDocId(doc: JsonApiDoc, id: string): JsonApiDoc {
  const clone = structuredClone(doc);
  const data = clone.data as JsonApiResource;
  const originalId = data.id;
  data.id = id;
  for (const resource of clone.included ?? []) {
    for (const rel of Object.values(resource.relationships ?? {})) {
      const linkage = (rel as { data?: unknown }).data;
      const repoint = (l: { type: string; id: string }) => {
        if (l.type === data.type && l.id === originalId) {
          l.id = id;
        }
      };
      if (Array.isArray(linkage)) {
        (linkage as Array<{ type: string; id: string }>).forEach(repoint);
      } else if (linkage) {
        repoint(linkage as { type: string; id: string });
      }
    }
  }
  return clone;
}

/**
 * Fetch-seam matchers for the SHIPPED TAS surfaces (builder reads carry no
 * SkipCache, so they bypass the request handler and land here).
 */
export function buildInProgressTasFetchMatchers(
  identity: DemoFixtureIdentity,
  paymentModelId: DemoTaPaymentModelId
): FetchMatcher[] {
  const catalog = getDemoTaProgramsForClient(identity.clientId);
  const templateResources = catalog.map(buildTemplateResource);
  const instanceResources = catalog.map(buildInstanceResource);
  const templateResource = templateResources[0] as JsonApiResource;
  const primaryInstance = instanceResources[0] as JsonApiResource;
  const companyResource = buildCompanyResource(identity.employerName);
  const companySettingResource = buildCompanySettingResource(paymentModelId);
  const employeeResource = buildEmployeeResource(identity);
  const courseResource = buildCourseResource(paymentModelId);
  const employeeGraph = [
    employeeResource,
    PARTICIPANT_RESOURCE,
    ...ELIGIBILITY_RESOURCES,
    companyResource,
    companySettingResource,
  ];
  const includedGraph = [courseResource, primaryInstance, templateResource, ...employeeGraph];
  // Two variants of the SAME application (same course/program/amounts, so the
  // approver's queue app mirrors what the employee submits): a DEFINE_COURSES
  // draft for the employee's own app, and a PENDING_COURSES_APPROVAL variant
  // for the approver's queue applications.
  const draftDoc: JsonApiDoc = {
    data: buildApplicationResource(paymentModelId, STATE_DRAFT),
    included: includedGraph,
  };
  const pendingDoc: JsonApiDoc = {
    data: buildApplicationResource(paymentModelId, STATE_PENDING),
    included: includedGraph,
  };
  // Per-id docs for the catalog templates + their instances — the shipped
  // reads (getting-started's findRecord, programs/show, programs/new) are
  // plain builders that land on THIS seam, and each id must resolve to ITS
  // OWN program (a single echoed doc would render the wrong program name for
  // three of the four cards).
  const templateDocsById = new Map(
    catalog.map((program, index) => [
      program.id,
      {
        data: templateResources[index] as JsonApiResource,
        included: [companyResource, companySettingResource],
      } satisfies JsonApiDoc,
    ])
  );
  const instanceDocsById = new Map(
    catalog.map((program, index) => [
      instanceIdFor(program.id),
      {
        data: instanceResources[index] as JsonApiResource,
        included: [
          templateResources[index] as JsonApiResource,
          ...employeeGraph,
          // The walkthrough instance links its in-flight application
          // (async:false — a dangling linkage renders as a missing record).
          ...(program.id === DEMO_TA_PRIMARY_TEMPLATE_ID
            ? [draftDoc.data as JsonApiResource, courseResource]
            : []),
        ],
      } satisfies JsonApiDoc,
    ])
  );
  const primaryInstanceDoc = instanceDocsById.get(
    instanceIdFor(DEMO_TA_PRIMARY_TEMPLATE_ID)
  ) as JsonApiDoc;

  return [
    // Application DETAIL builder read (`/tas-applications/<id>?include=…`,
    // never the /history subroute below). The id is echoed so links carrying
    // legacy mock-plane ids (demo-app-1, demo-queue-*-application) resolve.
    // Approver queue applications (demo-queue-*) resolve PENDING for the approve
    // flow; the employee's own application resolves as the DEFINE_COURSES draft.
    {
      urlMatches: (url) => /\/tas-applications\/[^/?]+(\?|$)/.test(url),
      method: 'GET',
      json: (url: string) => {
        const id = /\/tas-applications\/([^/?]+)/.exec(url)?.[1];
        const doc = id?.startsWith('demo-queue') ? pendingDoc : draftDoc;
        return id ? echoDocId(doc, id) : doc;
      },
    },
    // Program-template SINGLE read (getting-started's live path + admin
    // template views): each catalog id gets its own doc; unknown ids echo the
    // walkthrough template.
    {
      urlMatches: (url) => /\/tas-program-templates\/[^/?]+(\?|$)/.test(url),
      method: 'GET',
      json: (url: string) => {
        const id = /\/tas-program-templates\/([^/?]+)/.exec(url)?.[1];
        if (!id) {
          return templateDocsById.get(DEMO_TA_PRIMARY_TEMPLATE_ID);
        }
        return (
          templateDocsById.get(id) ??
          echoDocId(templateDocsById.get(DEMO_TA_PRIMARY_TEMPLATE_ID) as JsonApiDoc, id)
        );
      },
    },
    // Program-template COLLECTION (LAUNCHED + eligibility filters): the whole
    // catalog. The trial variant must stay empty or the dashboard's admin
    // merge de-dupes/duplicates cards.
    {
      urlMatches: (url) => /\/tas-program-templates(\?|$)/.test(url),
      method: 'GET',
      json: (url: string) =>
        /isTrial%5D=true|filter\[isTrial\]=true/.test(url)
          ? { data: [] }
          : {
              data: [...templateResources],
              included: [companyResource, companySettingResource],
            },
    },
    // Program-instance SINGLE read (programs/show + programs/new after a
    // start): the walkthrough instance is PROGRAM_APPROVED; the other
    // programs' instances are DRAFTs (the just-started state). Unknown ids
    // echo the walkthrough instance — this also covers legacy mock-plane ids.
    {
      urlMatches: (url) => /\/tas-program-instances\/[^/?]+(\?|$)/.test(url),
      method: 'GET',
      json: (url: string) => {
        const id = /\/tas-program-instances\/([^/?]+)/.exec(url)?.[1];
        if (!id) {
          return primaryInstanceDoc;
        }
        return instanceDocsById.get(id) ?? echoDocId(primaryInstanceDoc, id);
      },
    },
    // ACTIVE-instances query (getting-started/add-application guards): ONLY
    // the walkthrough program's approved instance — listing the startable
    // programs' drafts here would flip every card to "already enrolled".
    {
      urlMatches: (url) => /\/tas-program-instances(\?|$)/.test(url),
      method: 'GET',
      json: primaryInstanceDoc
        ? { data: [primaryInstanceDoc.data], included: primaryInstanceDoc.included }
        : { data: [] },
    },
    // Mutually-exclusive group lookup on the start path — no demo groups.
    {
      urlMatches: (url) => /\/tas-groups(\?|$)/.test(url),
      method: 'GET',
      json: { data: [] },
    },
    // The detail route's participant lookup
    // (`/tas-participants?filter[employee]=…&include=tasEligibilities`).
    {
      urlMatches: (url) => /\/tas-participants(\?|$)/.test(url),
      method: 'GET',
      json: { data: [PARTICIPANT_RESOURCE], included: [...ELIGIBILITY_RESOURCES] },
    },
  ];
}

// Eligibility maps are keyed by template CODE — a code missing from either map
// filters that program's card off the dashboard, so both maps cover the whole
// catalog (the industry-aware slot's two variants share one code).
const DYNAMIC_ELIGIBILITY = Object.fromEntries(DEMO_TA_PROGRAM_CODES.map((code) => [code, true]));
const PERIODS_OF_ELIGIBILITY = Object.fromEntries(
  DEMO_TA_PROGRAM_CODES.map((code) => [code, [['2024-01-01', '2027-12-31']]])
);

export const IN_PROGRESS_TAS_FETCH_MATCHERS: FetchMatcher[] = [
  { urlIncludes: '/dynamic-eligibility', json: DYNAMIC_ELIGIBILITY },
  { urlIncludes: '/periods-of-eligibility', json: PERIODS_OF_ELIGIBILITY },
  { urlIncludes: '/api/v2/limits/status', json: DEMO_LIMITS },
  { urlIncludes: '/api/v2/limits/summary', json: DEMO_LIMITS_SUMMARY },
  // The approval screens load each application's audit trail through the
  // TasApplicationAdapter's raw `getHistory` ajax (seam B, demoJsonFor).
  // `loadHistory` assigns `response.data` to `TasApplication.history`
  // ({ assistance_history, application_history }) — empty arrays render a
  // clean, empty activity timeline.
  {
    urlMatches: (url) => /\/tas-applications\/[^/]+\/history(?:\?|$)/.test(url),
    method: 'GET',
    json: { data: { assistance_history: [], application_history: [] } },
  },
];
